import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { formatMoney, formatDate } from "@/modules/config";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export function NotificationBell() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/notifications")
      .then(({ data }) => setData(data))
      .catch(() => setData({ items: [], count: 0 }));
  }, []);

  const items = data?.items ?? [];
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid="notification-btn" aria-label={t("Bildirimler")}
          className="relative h-9 w-9 rounded-md border border-slate-200 bg-white text-slate-500 hover:text-blue-600 hover:border-blue-300 inline-flex items-center justify-center transition-colors duration-150">
          <Bell className="h-4 w-4" />
          {items.length > 0 && (
            <span data-testid="notification-badge"
              className="absolute -top-1.5 -right-1.5 min-w-4 h-4 px-1 rounded-full bg-red-600 text-white text-[10px] font-semibold flex items-center justify-center">
              {items.length > 99 ? "99+" : items.length}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" data-testid="notification-panel">
        <p className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-[#0A1128]">
          {t("Bildirimler")} ({items.length})
        </p>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <p className="px-4 py-8 text-sm text-slate-400 text-center" data-testid="notification-empty">{t("Bildirim yok.")}</p>
          ) : (
            items.map((it, i) => (
              <div key={i} data-testid={`notification-item-${i}`}
                className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-50 hover:bg-slate-50 transition-colors duration-150">
                <span className={`h-2 w-2 rounded-full shrink-0 ${
                  it.overdue ? "bg-red-500" : it.kind === "payment" ? "bg-amber-500" : "bg-green-500"
                }`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[#0A1128] truncate">{it.title}</p>
                  <p className="text-[11px] text-slate-400">
                    {t(it.source)} · {formatDate(it.due_date)}
                    {it.overdue && <span className="text-red-600 font-medium"> · {t("Gecikti")}</span>}
                  </p>
                </div>
                <span className="text-sm font-semibold tabular text-[#0A1128]">{formatMoney(it.amount)}</span>
              </div>
            ))
          )}
        </div>
        <button type="button" onClick={() => { setOpen(false); navigate("/panel"); }}
          data-testid="notification-view-all"
          className="w-full px-4 py-2.5 text-xs font-medium text-blue-600 hover:bg-blue-50 border-t border-slate-100 transition-colors duration-150">
          {t("Tümünü Gör")}
        </button>
      </PopoverContent>
    </Popover>
  );
}
